import { useForm, useFieldArray, Controller } from "react-hook-form";
import { CrossIcon, SpinnerIcon } from "../Icons";
import {
  MailAlert,
  useAddMailAlertMutation,
  useGetMailAlertQuery,
  useUpdateMailAlertMutation,
} from "../../store/apiSlices/dealTrackerMailAlert";
import { useEffect } from "react";
import { toast } from "react-toastify";

type FormValues = {
  shipments_status: boolean;
  shipments_interval: string;
  payables_status: boolean;
  payables_interval: string;
  receivables_status: boolean;
  receivables_interval: string;
  email_list: { address: string }[];
};

const fields = [
  {
    label: "Shipments Status",
    key: "shipments_status",
  },
  {
    label: "Payables",
    key: "payables_status",
  },
  {
    label: "Receivables",
    key: "receivables_status",
  },
] as const;

// Extract type
type FieldKey = (typeof fields)[number]["key"];

const initialValue = {
  shipments_status: false,
  shipments_interval: "",
  payables_status: false,
  payables_interval: "",
  receivables_status: false,
  receivables_interval: "",
  email_list: [],
};

export default function MailAlertForm() {
  const {
    data: mailAlertData,
    isLoading,
    isError,
    refetch,
  } = useGetMailAlertQuery();

  const [addMailAlert] = useAddMailAlertMutation();
  const [updateMailAlert] = useUpdateMailAlertMutation();

  const mapServerData = (data: MailAlert) => {
    return {
      shipments_status: data.shipments_status === 1,
      shipments_interval: data.shipments_interval,
      payables_status: data.payables_status === 1,
      payables_interval: data.payables_interval,
      receivables_status: data.receivables_status === 1,
      receivables_interval: data.receivables_interval,
      email_list: data.email_list?.length
        ? data.email_list.map((email) => ({ address: email }))
        : [],
    };
  };

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm<FormValues>({
    defaultValues: initialValue,
  });

  const {
    fields: emailFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "email_list",
  });

  useEffect(() => {
    if (mailAlertData) {
      reset(mapServerData(mailAlertData));
    }
  }, [mailAlertData, reset]);

  const onSubmit = async (data: FormValues) => {
    const payload = {
      shipments_status: data.shipments_status ? 1 : 0,
      shipments_interval: data.shipments_interval,
      payables_status: data.payables_status ? 1 : 0,
      payables_interval: data.payables_interval,
      receivables_status: data.receivables_status ? 1 : 0,
      receivables_interval: data.receivables_interval,
      email_list: data.email_list.map((e) => e.address).filter(Boolean),
    };
    // console.log("Payload:", payload);

    if (mailAlertData && mailAlertData.id) {
      const { data } = await updateMailAlert(payload);

      if (data.status === "success") {
        toast.success(
          data?.message || "Mail notification updated successfully"
        );
      } else {
        toast.error("Something went wrong, please try again!");
      }
    } else {
      const { data } = await addMailAlert(payload);

      if (data.status === "success") {
        toast.success(
          data?.message || "Mail notification updated successfully"
        );
      } else {
        toast.error("Something went wrong, please try again!");
      }
    }
  };

  const isChecked = (key: FieldKey) => watch(key);

  if (isLoading) {
    return <MailAlertLoadingSkeleton />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <p className="text-gray-500 text-sm">Failed to load mail alert data</p>
        <button
          onClick={() => refetch()}
          className="mt-3 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* --- Alert Toggles Section --- */}
      {fields.map((section) => (
        <div
          key={section.key}
          className="grid items-center grid-cols-[2fr_1fr] gap-3"
        >
          <label className="w-full inline-flex items-center justify-between cursor-pointer">
            <span className="text-lg text-gray-700">{`${section.label} Alert`}</span>

            <Controller
              control={control}
              name={`${section.key}` as FieldKey}
              render={({ field: { value, onChange } }) => (
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                  <div className="w-14 h-8 bg-gray-200 rounded-full peer-checked:bg-blue-600 transition-colors"></div>
                  <div className="absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow peer-checked:translate-x-6 transition-transform"></div>
                </div>
              )}
            />
          </label>

          <select
            className="input-new cursor-pointer disabled:cursor-not-allowed"
            {...register(
              `${section.key.split("_")[0]}_interval` as keyof FormValues,
              {
                required: isChecked(section.key as FieldKey),
              }
            )}
            disabled={!isChecked(section.key as FieldKey)}
          >
            <option value="">Select Interval</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      ))}

      <div>
        <label className="text-lg font-medium text-gray-800">Send To</label>
        <div className="space-y-2 mt-2">
          {emailFields.map((field, index) => (
            <div key={field.id} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  placeholder="Enter email"
                  className={`input-new`}
                  {...register(`email_list.${index}.address`, {
                    required: "Email is required",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: "Please enter a valid email address",
                    },
                  })}
                />
                {
                  <button
                    type="button"
                    className="text-red-500 text-lg font-semibold"
                    onClick={() => remove(index)}
                  >
                    <CrossIcon className="size-5" />
                  </button>
                }
              </div>

              {/* Inline Error Message */}
              {errors.email_list?.[index]?.address && (
                <small className="text-red-500 text-xs ml-1">
                  {errors.email_list[index]?.address?.message}
                </small>
              )}
            </div>
          ))}

          <button
            type="button"
            className={`${
              emailFields.length >= 6 ? "text-gray-500" : "text-blue-600"
            } font-semibold text-sm mt-1 disabled:cursor-not-allowed`}
            onClick={() => append({ address: "" })}
            disabled={emailFields.length >= 6}
          >
            + Add Email
          </button>
        </div>
      </div>

      {/* --- Submit Button --- */}
      <div className="text-end pt-2">
        <button
          type="submit"
          className="inline-flex items-center gap-2 bg-custom-blue-medium text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-md disabled:bg-custom-blue-medium/80 hover:bg-custom-blue-medium/80 transition-all duration-200 active:scale-[0.97]"
          disabled={isSubmitting}
        >
          {isSubmitting && (
            <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-custom-blue-medium" />
          )}
          <span>Update</span>
        </button>
      </div>
    </form>
  );
}

function MailAlertLoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header Skeleton */}
      <div className="h-6 w-40 bg-gray-200 rounded" />

      {/* Email field skeletons */}
      <div className="space-y-3">
        <div className="flex flex-col gap-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-10 w-full bg-gray-200 rounded" />
              <div className="h-8 w-20 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>

      {/* Switches Section Skeleton */}
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="h-5 w-32 bg-gray-200 rounded" />
            <div className="h-5 w-10 bg-gray-200 rounded-full" />
          </div>
        ))}
      </div>

      {/* Submit Button Skeleton */}
      <div className="flex justify-end">
        <div className="h-9 w-24 bg-gray-200 rounded-xl" />
      </div>
    </div>
  );
}
